import { AdjustData } from '@/models/interfaces'
import { AuthUser } from '@/models/interfaces.auth'
import {
  logout as mixpanelLogout,
  _setMixpanelUserProperties,
} from '@/analytics/mixpanel'
import { storage } from '../helpers/storage'
import { daysDiff } from '@/helpers/time.native'
import { assertNotNull } from '@/helpers/typing'
import { socialLogout } from '@/models/auth/social'
import { Sentry } from '@/services/sentry'
import { resetAppCache } from '@/helpers/cache'
import { AB_KEY, AB_KEY_CID } from '@/services/ab/storage'
import { CHANGELOG_STORAGE_KEY } from '@/models/changelog'
import {
  FIRST_PAGE_KEY,
  HISTORY_KEY,
  REF_KEY,
  REF_DOMAIN_KEY,
} from '@/analytics/mixpanel/base'

const TOKEN_KEY = 'auth_token'
const USER_KEY = 'user'
const PREVIOUSLY_LOGIN_KEY = 'previously_logged_in'

// User-specific localStorage keys that should be cleared on logout
export const USER_SPECIFIC_STORAGE_KEYS = [
  AB_KEY_CID,
  AB_KEY,
  CHANGELOG_STORAGE_KEY,
  FIRST_PAGE_KEY,
  HISTORY_KEY,
  REF_KEY,
  REF_DOMAIN_KEY,
]

export function loggedIn(): boolean {
  return getToken() !== null
}

export function previouslyLoggedIn(): boolean {
  return !loggedIn() && storage.getItem(PREVIOUSLY_LOGIN_KEY) === 'true'
}

export function getToken(): string | null {
  return storage.getItem(TOKEN_KEY)
}

export function getUserId(): string | null {
  const token = getToken()
  if (!token) {
    return null
  }
  return JSON.parse(atob(token.split('.')[0]))[0]
}

export function getUser(): AuthUser | null {
  const data = storage.getItem(USER_KEY)
  if (data) {
    const user = JSON.parse(data)
    return user
  }
  return null
}

export function getUsername(): string | null {
  const user = getUser()
  if (user) {
    return user.username
  }
  return null
}

export function setUsername(username: string): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set username, no user')
  }
  user.username = username
  storage.setItem(USER_KEY, JSON.stringify(user))
}

export function setGa4ClientId(ga4_client_id: string): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set ga4_client_id, no user')
  }
  user.ga4_client_id = ga4_client_id
  storage.setItem(USER_KEY, JSON.stringify(user))
}

export function setAdjustData(newAdjustData: AdjustData): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set idfa, no user')
  }
  if (newAdjustData.adid_android) {
    user.adid_android = newAdjustData.adid_android
  }
  if (newAdjustData.adid_ios) {
    user.adid_ios = newAdjustData.adid_ios
  }
  if (newAdjustData.adid_web) {
    user.adid_web = newAdjustData.adid_web
  }
  if (newAdjustData.gps_adid) {
    user.gps_adid = newAdjustData.gps_adid
  }
  if (newAdjustData.idfa) {
    user.idfa = newAdjustData.idfa
  }
  storage.setItem(USER_KEY, JSON.stringify(user))
}

export function getEmail(): string | null {
  const user = getUser()
  if (user) {
    return user.email
  }
  return null
}

export function setEmail(email: string): void {
  const user = getUser()
  if (!user) {
    throw new Error('Can not set email, no user')
  }
  user.email = email
  storage.setItem(USER_KEY, JSON.stringify(user))
}

export function isPaidPlan(): boolean {
  const user = getUser()
  if (!user) {
    return false
  }

  if (user.is_limited) {
    return false
  }
  return true
}

export function isFreePlan(): boolean {
  return !isPaidPlan()
}

export function isNewUser(): boolean {
  const user = assertNotNull(getUser())

  // Paid user
  if (!user.is_limited) {
    // Is new when trial period is active
    return user.is_trial
  } else {
    // The free_plan (or subscription_bad)
    // is new when within 2 weeks of signup
    return !(daysDiff(user.signup) >= 14)
  }
}

export function isFirstVisit(): boolean {
  return !loggedIn() && !previouslyLoggedIn()
}

export function isShortformUser(): boolean {
  const email = getEmail()
  return email !== null && email.endsWith('@shortform.com')
}

export function isTemporaryUser(): boolean {
  const user = getUser()

  if (!user) {
    return false
  }

  return user.temporary_account_state === 'temporary'
}

export function updateAuthUser(
  data: AuthUser,
  isSignupIntercept?: boolean,
): void {
  if (isUserChanged(data)) {
    storage.setItem(USER_KEY, JSON.stringify(data))

    if ((window as any)['__PRERENDER_INJECTED']) {
      // Don't use Mixpanel during prerendering.
      return
    }

    if (isSignupIntercept) {
      // Don't update the user in Mixpanel when intercepting a signup request
      // because that could create a duplicate user. For more details, see:
      // https://github.com/shortformhq/main/issues/14950#issuecomment-2621066719
      return
    }

    try {
      _setMixpanelUserProperties(data)
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

export function saveAuth(token: string, user: AuthUser): void {
  storage.setItem(TOKEN_KEY, token)
  storage.setItem(USER_KEY, JSON.stringify(user))
  storage.removeItem(PREVIOUSLY_LOGIN_KEY)
}

export function removeAuth(setLogoutFlag: boolean = true): void {
  if (loggedIn() && setLogoutFlag) {
    // Set a flag to indicate that a user was previously logged in.
    // This condition is `true` by default, but we have the option
    // to turn it off during tests to reset the auth data in local storage.
    storage.setItem(PREVIOUSLY_LOGIN_KEY, 'true')
  }

  storage.removeItem(TOKEN_KEY)
  storage.removeItem(USER_KEY)
}

export function isUserChanged(data: AuthUser): boolean {
  const user = getUser()
  if (!user) {
    return true
  } else {
    for (const key in data) {
      if (!(key in user)) {
        return true
      }
      // @ts-ignore: element explicitly has an 'any' type
      // because type AuthUser has no index signature.
      if (data[key] !== user[key]) {
        return true
      }
    }
  }
  return false
}

/**
 * Logout without calling the `/api/auth/logout` endpoint. Removes any
 * auth data from the browser / native device.
 */
export async function localLogout(): Promise<void> {
  removeAuth()
  await socialLogout()

  // Clear all app caches on logout
  // All caches contain user data, so we need to clear everything
  try {
    await resetAppCache()
    clearUserLocalStorage()
  } catch (error) {
    Sentry.captureException(error)
  }

  try {
    mixpanelLogout()
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Clear user-specific local storage items.
 */
export function clearUserLocalStorage(): void {
  USER_SPECIFIC_STORAGE_KEYS.forEach((key) => {
    storage.removeItem(key)
  })
}

/**
 * Get the user's signup date.
 */
export function getSignupDate(): Date | null {
  const user = getUser()
  if (!user) {
    return null
  }

  return new Date(user.signup)
}

export * as auth from './auth'
